import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Phone, MessageCircle, MapPin, Globe, Instagram, Share2, Copy, ExternalLink, IndianRupee, Eye, ShoppingCart, X, BadgeCheck, Store, CreditCard, ShieldCheck } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { waLink, telLink } from '@/lib/phone';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Storefront() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const partnerCode = searchParams.get('p') || '';
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  // Order placement state
  const [orderOpen, setOrderOpen] = useState(false);
  const [orderForm, setOrderForm] = useState({ customer_name: '', customer_phone: '', customer_address: '', quantity: 1, note: '' });
  const [placing, setPlacing] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const url = `${API_BASE}/storefront/${slug}${partnerCode ? `?p=${encodeURIComponent(partnerCode)}` : ''}`;
        const { data } = await axios.get(url);
        if (!cancelled) {
          setDeal(data);
          document.title = `${data.brand_name || data.title} · INDIVALIFE`;
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? 'not_found' : 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, partnerCode]);

  const placeOrder = async () => {
    if (!orderForm.customer_name.trim() || !orderForm.customer_phone.trim()) {
      toast.error('Name and phone are required');
      return;
    }
    setPlacing(true);
    try {
      const { data } = await axios.post(`${API_BASE}/storefront/order`, {
        slug,
        partner_code: partnerCode || undefined,
        customer_name: orderForm.customer_name.trim(),
        customer_phone: orderForm.customer_phone.trim(),
        customer_address: orderForm.customer_address.trim() || undefined,
        quantity: Math.max(1, Number(orderForm.quantity) || 1),
        note: orderForm.note.trim() || undefined,
      });
      setPlacedOrder(data);
      toast.success('Order placed! The seller will reach out shortly.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not place order');
    } finally {
      setPlacing(false);
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-sm text-[var(--bz-muted)]">Loading store…</div>;
  if (error === 'not_found') return (
    <div className="min-h-screen grid place-items-center px-6 text-center">
      <div>
        <div className="text-6xl mb-3">🛍️</div>
        <h1 className="text-2xl font-black display">Store not found</h1>
        <p className="text-sm text-[var(--bz-muted)] mt-2">This storefront may have been removed or the link is incorrect.</p>
        <a href="/" className="inline-block mt-4 text-[#1E3A8A] underline">Go to INDIVALIFE</a>
      </div>
    </div>
  );
  if (error || !deal) return <div className="min-h-screen grid place-items-center">Something went wrong.</div>;

  const accent = deal.brand_color || '#1D4ED8';
  const url = window.location.href;
  const discountPct = deal.mrp ? Math.round(((deal.mrp - deal.offer_price) / deal.mrp) * 100) : 0;
  const images = deal.images || [];
  const cover = images[activeImg] || images[0];
  const qty = Math.max(1, Number(orderForm.quantity) || 1);
  const orderTotal = (deal.offer_price * qty).toFixed(2);
  const brandLabel = deal.brand_name || deal.owner_name || 'this seller';

  const shareText = `Check out ${deal.brand_name || deal.title} on INDIVALIFE — ${deal.title} at ₹${deal.offer_price}\n${url}`;

  const onShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: deal.brand_name || deal.title, text: shareText, url }); return; } catch (_e) { /* fall through */ }
    }
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const waLinkInquiry = waLink(deal.whatsapp_number || deal.contact_phone, `Hi ${deal.brand_name || deal.owner_name}, I'd like to know more about: ${deal.title}`);
  const waShareLink = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-right" />

      {/* Header banner */}
      <div className="text-white" style={{ background: `linear-gradient(135deg, ${accent}, #0F172A)` }}>
        <div className="max-w-screen-md mx-auto px-5 py-6 flex items-center gap-4">
          {deal.brand_logo_url ? (
            <img src={deal.brand_logo_url} alt={deal.brand_name} className="h-16 w-16 rounded-xl bg-white p-1 object-contain shadow-2xl" />
          ) : (
            <div className="h-16 w-16 rounded-xl bg-white text-2xl font-black display grid place-items-center" style={{ color: accent }}>
              {(deal.brand_name || deal.title || '?').slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] opacity-80 font-bold">INDIVALIFE · Brand store</div>
            <div className="text-2xl sm:text-3xl font-black display truncate" data-testid="brand-name">{deal.brand_name || deal.owner_name}</div>
            {deal.brand_tagline && <div className="text-sm opacity-90">{deal.brand_tagline}</div>}
          </div>
          <button onClick={onShare} className="bg-white/15 hover:bg-white/25 px-3 py-2 rounded-lg flex items-center gap-2 text-sm" data-testid="share-store-btn">
            <Share2 className="h-4 w-4" />Share
          </button>
        </div>
      </div>

      {/* Sold-by partner banner */}
      {deal.sold_by && (
        <div className="max-w-screen-md mx-auto px-5 mt-3">
          <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg" style={{ background: `${accent}1A`, color: accent }} data-testid="sold-by-partner-banner">
            <BadgeCheck className="h-4 w-4" />
            <span>Sold by authorised partner <b>{deal.sold_by}</b></span>
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="max-w-screen-md mx-auto px-5 py-6">
        {cover && (
          <div className="rounded-2xl overflow-hidden shadow-xl bg-[var(--bz-soft, #f5f5f4)]">
            <img src={cover} alt={deal.title} className="w-full max-h-[480px] object-contain bg-white" data-testid="cover-image" />
          </div>
        )}
        {images.length > 1 && (
          <div className="flex gap-2 mt-3 overflow-x-auto">
            {images.map((img, i) => (
              <button key={i} onClick={() => setActiveImg(i)} className={`shrink-0 rounded-lg overflow-hidden border-2 ${i === activeImg ? 'border-[var(--bz-green,#1D4ED8)]' : 'border-transparent'}`} style={{ borderColor: i === activeImg ? accent : 'transparent' }} data-testid={`thumb-${i}`}>
                <img src={img} alt="" className="h-16 w-16 object-cover" />
              </button>
            ))}
          </div>
        )}

        <div className="mt-6">
          <h1 className="text-2xl sm:text-3xl font-black display leading-tight" data-testid="deal-title">{deal.title}</h1>
          <div className="flex items-baseline gap-3 mt-3 flex-wrap">
            <div className="text-3xl font-black display flex items-center" style={{ color: accent }} data-testid="offer-price">
              <IndianRupee className="h-6 w-6" />{deal.offer_price}
            </div>
            {deal.mrp > deal.offer_price && (
              <>
                <div className="text-lg text-[var(--bz-muted, #6b7280)] line-through">₹{deal.mrp}</div>
                <span className="text-xs font-bold text-white px-2 py-1 rounded-full" style={{ background: accent }}>{discountPct}% OFF</span>
              </>
            )}
          </div>
          <p className="text-[15px] leading-relaxed mt-4 whitespace-pre-line" data-testid="deal-description">{deal.description}</p>

          {deal.address && (
            <div className="flex items-start gap-2 mt-4 text-sm text-[var(--bz-muted)]">
              <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
              <div>{deal.address}<br />{deal.owner_town} · {deal.owner_pincode}</div>
            </div>
          )}

          {/* Action buttons */}
          <div className="grid grid-cols-2 gap-3 mt-6">
            <button onClick={() => setOrderOpen(true)} className="h-12 rounded-xl text-white font-bold flex items-center justify-center gap-2 shadow-lg col-span-2" style={{ background: accent }} data-testid="place-order-btn">
              <ShoppingCart className="h-4 w-4" />Order now · ₹{deal.offer_price}
            </button>
            {(deal.whatsapp_number || deal.contact_phone) && (
              <a href={waLinkInquiry} target="_blank" rel="noopener noreferrer" className="h-12 rounded-xl text-white font-bold flex items-center justify-center gap-2 shadow-lg" style={{ background: '#25D366' }} data-testid="whatsapp-contact-btn">
                <MessageCircle className="h-4 w-4" />WhatsApp
              </a>
            )}
            {deal.contact_phone && (
              <a href={telLink(deal.contact_phone)} className="h-12 rounded-xl font-bold flex items-center justify-center gap-2 shadow border" style={{ borderColor: accent, color: accent }} data-testid="call-contact-btn">
                <Phone className="h-4 w-4" />Call
              </a>
            )}
          </div>

          {/* Brand socials */}
          {(deal.instagram_handle || deal.website_url) && (
            <div className="mt-5 flex gap-3 flex-wrap">
              {deal.instagram_handle && (
                <a href={`https://instagram.com/${deal.instagram_handle.replace(/^@/, '')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-pink-50 text-pink-700" data-testid="instagram-link"><Instagram className="h-4 w-4" />@{deal.instagram_handle.replace(/^@/, '')}</a>
              )}
              {deal.website_url && (
                <a href={deal.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-blue-50 text-blue-700" data-testid="website-link"><Globe className="h-4 w-4" />{deal.website_url.replace(/^https?:\/\//, '')}<ExternalLink className="h-3 w-3" /></a>
              )}
            </div>
          )}

          {/* Payment options — pay seller directly */}
          {(deal.upi || deal.phonepe || deal.qr_image || (deal.bank_details && (deal.bank_details.account || deal.bank_details.bank))) && (
            <div className="mt-8 p-5 rounded-2xl border-2" style={{ borderColor: `${accent}33`, background: `${accent}08` }} data-testid="payment-block">
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" style={{ color: accent }} />
                <h3 className="text-lg font-black display">Pay {brandLabel}</h3>
              </div>
              <p className="text-xs text-[var(--bz-muted)] mt-1">Pay directly to the seller and share the UTR via WhatsApp after.</p>

              <div className="grid sm:grid-cols-[auto_1fr] gap-4 mt-4">
                {deal.qr_image && (
                  <div className="bg-white p-2 rounded-xl border shadow-sm">
                    <img src={deal.qr_image} alt="Payment QR" className="h-36 w-36 sm:h-40 sm:w-40 object-contain" data-testid="payment-qr" />
                    <div className="text-[10px] text-center text-[var(--bz-muted)] mt-1">Scan to pay</div>
                  </div>
                )}
                <div className="space-y-2 text-sm">
                  {deal.upi && (
                    <div className="flex items-center justify-between gap-2 bg-white rounded-lg px-3 py-2 border">
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase font-bold text-[var(--bz-muted)]">UPI</div>
                        <div className="font-mono text-sm truncate" data-testid="payment-upi">{deal.upi}</div>
                      </div>
                      <button onClick={() => { navigator.clipboard.writeText(deal.upi); toast.success('UPI copied'); }} className="text-xs font-bold px-2 py-1 rounded" style={{ color: accent }} data-testid="copy-upi-btn"><Copy className="h-3 w-3" /></button>
                    </div>
                  )}
                  {deal.phonepe && (
                    <div className="flex items-center justify-between gap-2 bg-white rounded-lg px-3 py-2 border">
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase font-bold text-[var(--bz-muted)]">PhonePe</div>
                        <div className="font-mono text-sm truncate" data-testid="payment-phonepe">{deal.phonepe}</div>
                      </div>
                      <button onClick={() => { navigator.clipboard.writeText(deal.phonepe); toast.success('PhonePe copied'); }} className="text-xs px-2 py-1 rounded" style={{ color: accent }}><Copy className="h-3 w-3" /></button>
                    </div>
                  )}
                  {deal.bank_details && (deal.bank_details.account || deal.bank_details.bank) && (
                    <div className="bg-white rounded-lg px-3 py-2 border" data-testid="payment-bank">
                      <div className="text-[10px] uppercase font-bold text-[var(--bz-muted)]">Bank transfer</div>
                      {deal.bank_details.bank && <div className="text-xs"><b>Bank:</b> {deal.bank_details.bank}</div>}
                      {deal.bank_details.account && <div className="text-xs"><b>A/C:</b> <span className="font-mono">{deal.bank_details.account}</span></div>}
                      {deal.bank_details.ifsc && <div className="text-xs"><b>IFSC:</b> <span className="font-mono">{deal.bank_details.ifsc}</span></div>}
                    </div>
                  )}
                  {(deal.whatsapp_number || deal.contact_phone) && (
                    <a href={waLink(deal.whatsapp_number || deal.contact_phone, `Hi ${brandLabel}, I'd like to pay ₹${deal.offer_price} for "${deal.title}". Please confirm the order.`)} target="_blank" rel="noopener noreferrer" className="inline-flex w-full items-center justify-center gap-2 mt-1 h-10 rounded-lg text-white font-bold text-sm shadow" style={{ background: '#25D366' }} data-testid="wa-pay-seller-btn">
                      <MessageCircle className="h-4 w-4" />Confirm on WhatsApp · ₹{deal.offer_price}
                    </a>
                  )}
                </div>
              </div>

              <div className="mt-3 flex items-center gap-1.5 text-[11px] text-[var(--bz-muted)]">
                <ShieldCheck className="h-3 w-3" style={{ color: accent }} />
                INDIVALIFE never holds your money — payments go straight to the seller.
              </div>
            </div>
          )}

          {/* More from this brand */}
          {deal.owner_personal_id && (
            <Link to={`/store/${deal.owner_personal_id}`} className="mt-6 flex items-center justify-between gap-3 p-4 rounded-2xl border hover:shadow-lg transition" style={{ borderColor: `${accent}40` }} data-testid="brand-store-link">
              <div className="flex items-center gap-3 min-w-0">
                {deal.brand_logo_url || deal.owner_picture ? (
                  <img src={deal.brand_logo_url || deal.owner_picture} alt={brandLabel} className="h-12 w-12 rounded-lg object-cover border" />
                ) : (
                  <div className="h-12 w-12 rounded-lg grid place-items-center font-black" style={{ background: `${accent}1A`, color: accent }}>
                    {brandLabel.slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-[10px] uppercase font-bold tracking-wider text-[var(--bz-muted)]">Brand store</div>
                  <div className="font-black truncate">{brandLabel}</div>
                  <div className="text-xs text-[var(--bz-muted)]">See all products from this seller</div>
                </div>
              </div>
              <div className="text-xs font-bold inline-flex items-center gap-1" style={{ color: accent }}>
                <Store className="h-4 w-4" />Visit
              </div>
            </Link>
          )}

          {/* Share row */}
          <div className="mt-8 p-4 bg-[var(--bz-soft, #f5f5f4)] rounded-xl">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[var(--bz-muted)]">Share this store</div>
            <div className="flex flex-wrap gap-2 mt-3">
              <a href={waShareLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg text-white" style={{ background: '#25D366' }} data-testid="share-whatsapp-btn">
                <MessageCircle className="h-4 w-4" />WhatsApp
              </a>
              <button onClick={onShare} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-white border" data-testid="share-native-btn"><Share2 className="h-4 w-4" />More…</button>
              <button onClick={() => { navigator.clipboard.writeText(url); toast.success('Link copied'); }} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-white border" data-testid="copy-link-btn"><Copy className="h-4 w-4" />Copy link</button>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between text-xs text-[var(--bz-muted)]">
            <div className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />{deal.views || 1} views</div>
            <a href="/" className="text-[#1E3A8A]">Powered by INDIVALIFE ›</a>
          </div>

          {/* Spacer for sticky bottom bar */}
          <div className="h-20 sm:hidden" />
        </div>
      </div>

      {/* Sticky bottom CTA — mobile only */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 border-t bg-white p-3 shadow-2xl flex items-center gap-3" data-testid="sticky-cta-bar">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase font-bold text-[var(--bz-muted)]">Total</div>
          <div className="text-xl font-black flex items-center" style={{ color: accent }}><IndianRupee className="h-4 w-4" />{deal.offer_price}</div>
        </div>
        {(deal.whatsapp_number || deal.contact_phone) && (
          <a href={waLinkInquiry} target="_blank" rel="noopener noreferrer" className="h-11 px-3 rounded-lg flex items-center gap-1.5 text-white text-sm font-bold shadow" style={{ background: '#25D366' }} data-testid="sticky-wa-btn">
            <MessageCircle className="h-4 w-4" />Chat
          </a>
        )}
        <button onClick={() => setOrderOpen(true)} className="h-11 px-4 rounded-lg text-white text-sm font-black flex items-center gap-1.5 shadow" style={{ background: accent }} data-testid="sticky-order-btn">
          <ShoppingCart className="h-4 w-4" />Order
        </button>
      </div>

      {/* Order modal */}
      {orderOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center px-4" onClick={() => !placing && !placedOrder && setOrderOpen(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-5 shadow-2xl relative" onClick={e => e.stopPropagation()} data-testid="order-modal">
            <button onClick={() => { setOrderOpen(false); setPlacedOrder(null); }} className="absolute top-3 right-3 text-[var(--bz-muted)]"><X className="h-5 w-5" /></button>

            {!placedOrder ? (
              <>
                <h2 className="text-xl font-black display">Place your order</h2>
                <p className="text-xs text-[var(--bz-muted)] mt-1">{deal.title} · ₹{deal.offer_price} per unit</p>

                <div className="space-y-3 mt-4">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wide text-[var(--bz-muted)]">Your name *</label>
                    <input className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" value={orderForm.customer_name} onChange={e => setOrderForm(f => ({ ...f, customer_name: e.target.value }))} data-testid="order-name-input" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wide text-[var(--bz-muted)]">Phone (WhatsApp) *</label>
                    <input className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" value={orderForm.customer_phone} onChange={e => setOrderForm(f => ({ ...f, customer_phone: e.target.value }))} inputMode="tel" data-testid="order-phone-input" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wide text-[var(--bz-muted)]">Delivery address</label>
                    <textarea className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" rows={2} value={orderForm.customer_address} onChange={e => setOrderForm(f => ({ ...f, customer_address: e.target.value }))} data-testid="order-address-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wide text-[var(--bz-muted)]">Quantity</label>
                      <input type="number" min={1} className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" value={orderForm.quantity} onChange={e => setOrderForm(f => ({ ...f, quantity: e.target.value }))} data-testid="order-qty-input" />
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-bold uppercase tracking-wide text-[var(--bz-muted)]">Total</div>
                      <div className="text-2xl font-black display" style={{ color: accent }} data-testid="order-total">₹{orderTotal}</div>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wide text-[var(--bz-muted)]">Note (optional)</label>
                    <input className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" value={orderForm.note} onChange={e => setOrderForm(f => ({ ...f, note: e.target.value }))} placeholder="Size, color, delivery slot…" data-testid="order-note-input" />
                  </div>
                </div>

                <button onClick={placeOrder} disabled={placing} className="w-full mt-5 h-12 rounded-xl text-white font-bold shadow-lg disabled:opacity-60" style={{ background: accent }} data-testid="confirm-order-btn">
                  {placing ? 'Placing…' : `Confirm order · ₹${orderTotal}`}
                </button>
                <p className="text-[10px] text-center text-[var(--bz-muted)] mt-2">The seller will contact you on WhatsApp to confirm payment & delivery.</p>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="text-5xl mb-2">✅</div>
                <h2 className="text-xl font-black display">Order placed!</h2>
                <p className="text-sm text-[var(--bz-muted)] mt-2">Your order ID: <span className="font-mono text-[var(--bz-text)]">{placedOrder.order_id}</span></p>
                <p className="text-sm text-[var(--bz-muted)] mt-1">Total: <b style={{ color: accent }}>₹{placedOrder.total_amount}</b></p>
                <p className="text-xs text-[var(--bz-muted)] mt-3">The seller has been notified and will reach out shortly.</p>
                <div className="flex gap-2 mt-4">
                  {(deal.whatsapp_number || deal.contact_phone) && (
                    <a href={waLink(deal.whatsapp_number || deal.contact_phone, `Hi! I just placed order #${placedOrder.order_id} for ${deal.title}.`)} target="_blank" rel="noopener noreferrer" className="flex-1 h-11 rounded-xl text-white font-bold flex items-center justify-center gap-2" style={{ background: '#25D366' }} data-testid="order-wa-seller-btn">
                      <MessageCircle className="h-4 w-4" />Message seller
                    </a>
                  )}
                  <button onClick={() => { setOrderOpen(false); setPlacedOrder(null); setOrderForm({ customer_name: '', customer_phone: '', customer_address: '', quantity: 1, note: '' }); }} className="flex-1 h-11 rounded-xl border font-bold" style={{ borderColor: accent, color: accent }} data-testid="order-done-btn">Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
